import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Plus, Loader2 } from "lucide-react";
import { fetchReviews, adminAddReview, adminDeleteReview, formatApiErrorDetail } from "@/api";

const EMPTY = { name: "", rating: 5, text: "" };

export default function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetchReviews().then(setReviews).catch(() => {});
  }, []);

  useEffect(() => load(), [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminAddReview({ ...form, rating: Number(form.rating) });
      toast.success("Review published to the homepage");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this review from the homepage?")) return;
    try {
      await adminDeleteReview(id);
      toast.success("Review removed");
      load();
    } catch {
      toast.error("Could not remove review");
    }
  };

  return (
    <div data-testid="reviews-tab" className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <form onSubmit={submit} data-testid="review-form" className="lg:col-span-2 bg-[#122659] border border-sky-300/20 rounded-xl p-8 space-y-5 h-fit">
        <h2 className="font-display text-xl font-black text-white flex items-center gap-2">
          <Plus size={20} className="text-[#F57C00]" /> Add a review
        </h2>
        <div>
          <label className="input-label" htmlFor="rv-name">Customer name</label>
          <input id="rv-name" data-testid="review-name-input" required className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sipho D." />
        </div>
        <div>
          <label className="input-label" htmlFor="rv-rating">Rating</label>
          <select id="rv-rating" data-testid="review-rating-select" className="input-field" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })}>
            {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} star{r > 1 ? "s" : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="input-label" htmlFor="rv-text">Review text</label>
          <textarea id="rv-text" data-testid="review-text-input" required rows={4} className="input-field resize-none" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="What did the customer say?" />
        </div>
        <button type="submit" data-testid="review-submit-btn" disabled={saving} className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-3.5 rounded-md transition-colors duration-300 flex items-center justify-center gap-2">
          {saving && <Loader2 size={16} className="animate-spin" />}
          {saving ? "Publishing…" : "Publish review"}
        </button>
      </form>

      <div className="lg:col-span-3 space-y-4" data-testid="reviews-list">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-[#122659] border border-sky-300/20 rounded-xl p-6 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <p className="font-bold text-white text-sm">{r.name}</p>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={12} className={s <= r.rating ? "text-[#F57C00] fill-[#F57C00]" : "text-slate-600"} />
                  ))}
                </div>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">"{r.text}"</p>
            </div>
            <button
              data-testid={`review-delete-${r.id}`}
              onClick={() => remove(r.id)}
              className="p-2.5 rounded-md border border-red-400/40 text-red-400 hover:bg-red-400 hover:text-white transition-colors shrink-0"
              aria-label="Delete review"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        {reviews.length === 0 && (
          <p className="text-slate-400 text-sm bg-[#122659] border border-sky-300/20 rounded-xl p-8 text-center" data-testid="reviews-empty">
            No reviews yet — add your first customer review and it appears on the homepage.
          </p>
        )}
      </div>
    </div>
  );
}
